import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { 
  Loader2, 
  Search, 
  X, 
  Download,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  FileQuestion,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { PageHeader } from '@/components/admin/PageHeader';
import { StudioSurface } from '@/components/admin/StudioSurface';
import { EmptyBlock } from '@/components/admin/EmptyBlock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SystemEventLevelBadge } from '@/components/admin/SystemEventLevelBadge';
import { SystemEventSourceBadge } from '@/components/admin/SystemEventSourceBadge';
import { SystemEventDetailDialog } from '@/components/admin/SystemEventDetailDialog';
import {
  useSystemEvents,
  type SystemEvent,
  type SystemEventFilters,
  type SystemEventLevel,
  type SystemEventSource,
} from '@/hooks/useSystemEvents';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { useToast } from '@/hooks/use-toast';

type TimeRange = '24h' | '7d' | '30d';

export default function AdminSystemLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Parse initial filters from URL
  const initialFilters: SystemEventFilters = {
    search: searchParams.get('search') || '',
    level: (searchParams.get('level') as SystemEventLevel | 'all') || 'all',
    source: (searchParams.get('source') as SystemEventSource | 'all') || 'all',
    domain: searchParams.get('domain') || 'all',
    producerId: searchParams.get('producerId') || undefined,
    range: (searchParams.get('range') as TimeRange) || '24h',
    requestId: searchParams.get('requestId') || undefined,
  };

  const [filters, setFilters] = useState<SystemEventFilters>(initialFilters);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [selectedEvent, setSelectedEvent] = useState<SystemEvent | null>(null);

  // Get producers for filter dropdown
  const { producers } = useAdminProducerContext();

  const { data, isLoading, error } = useSystemEvents(filters, page);

  const isUnauthorized = error?.message === 'Unauthorized';

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.level && filters.level !== 'all') params.set('level', filters.level);
    if (filters.source && filters.source !== 'all') params.set('source', filters.source);
    if (filters.domain && filters.domain !== 'all') params.set('domain', filters.domain);
    if (filters.producerId) params.set('producerId', filters.producerId);
    if (filters.range && filters.range !== '24h') params.set('range', filters.range);
    if (filters.requestId) params.set('requestId', filters.requestId);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const updateFilter = <K extends keyof SystemEventFilters>(key: K, value: SystemEventFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleSearch = () => {
    updateFilter('search', searchInput);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      level: 'all',
      source: 'all',
      domain: 'all',
      producerId: undefined,
      range: '24h',
      requestId: undefined,
    });
    setSearchInput('');
    setPage(0);
  };

  const hasActiveFilters = 
    filters.search || 
    (filters.level && filters.level !== 'all') ||
    (filters.source && filters.source !== 'all') ||
    (filters.domain && filters.domain !== 'all') ||
    filters.producerId ||
    filters.requestId;

  const handleExportCSV = () => {
    if (!data?.events?.length) return;

    const headers = ['Tid', 'Nivå', 'Källa', 'Domän', 'Händelsenyckel', 'Meddelande', 'Värd', 'Request ID'];
    const rows = data.events.slice(0, 500).map(e => [
      format(new Date(e.created_at), 'yyyy-MM-dd HH:mm:ss'),
      e.level,
      e.source,
      e.domain || '',
      e.event_key,
      e.message.replace(/"/g, '""'),
      e.producer?.name || '',
      e.request_id || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `systemloggar-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Export klar',
      description: `${Math.min(data.events.length, 500)} rader exporterades.`,
    });
  };

  const formatTime = (date: string) => {
    return format(new Date(date), "d MMM HH:mm:ss", { locale: sv });
  };

  if (isUnauthorized) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Åtkomst nekad</h2>
          <p className="text-muted-foreground mb-6">
            Du saknar behörighet att se systemloggar.
          </p>
          <Button asChild variant="outline">
            <Link to="/app/admin/producers">Tillbaka till värdar</Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Systemloggar"
        description="Sök och filtrera bland systemhändelser"
        actions={
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportCSV}
            disabled={!data?.events?.length}
          >
            <Download className="h-4 w-4 mr-2" />
            Exportera CSV
          </Button>
        }
      />

      <div className="px-4 lg:px-8 pb-8 space-y-6">

        {/* Filters */}
        <StudioSurface>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Sök i meddelande eller händelsenyckel..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 pr-9"
                  />
                  {searchInput && (
                    <button
                      onClick={() => {
                        setSearchInput('');
                        updateFilter('search', '');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Level */}
              <Select 
                value={filters.level || 'all'} 
                onValueChange={(v) => updateFilter('level', v as SystemEventLevel | 'all')}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Nivå" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla nivåer</SelectItem>
                  <SelectItem value="error">Fel</SelectItem>
                  <SelectItem value="warn">Varning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>

              {/* Source */}
              <Select 
                value={filters.source || 'all'} 
                onValueChange={(v) => updateFilter('source', v as SystemEventSource | 'all')}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Källa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla källor</SelectItem>
                  <SelectItem value="ui">UI</SelectItem>
                  <SelectItem value="edge">Edge</SelectItem>
                  <SelectItem value="db">DB</SelectItem>
                  <SelectItem value="cron">Cron</SelectItem>
                </SelectContent>
              </Select>

              {/* Domain */}
              <Select 
                value={filters.domain || 'all'} 
                onValueChange={(v) => updateFilter('domain', v)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Domän" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla domäner</SelectItem>
                  <SelectItem value="auth">auth</SelectItem>
                  <SelectItem value="producer">producer</SelectItem>
                  <SelectItem value="marketplace">marketplace</SelectItem>
                  <SelectItem value="booking">booking</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                  <SelectItem value="system">system</SelectItem>
                </SelectContent>
              </Select>

              {/* Producer */}
              <Select 
                value={filters.producerId || 'all'} 
                onValueChange={(v) => updateFilter('producerId', v === 'all' ? undefined : v)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Värd" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla värdar</SelectItem>
                  {producers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Time range */}
              <Select 
                value={filters.range || '24h'} 
                onValueChange={(v) => updateFilter('range', v as TimeRange)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Tidsperiod" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Senaste 24 timmarna</SelectItem>
                  <SelectItem value="7d">Senaste 7 dagarna</SelectItem>
                  <SelectItem value="30d">Senaste 30 dagarna</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Rensa filter
                </Button>
              )}
            </div>

            {/* Active request_id filter */}
            {filters.requestId && (
              <div className="mt-4 flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">
                  request_id: {filters.requestId}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => updateFilter('requestId', undefined)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </StudioSurface>

        {/* Results */}
        <StudioSurface className="p-0 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : data?.events && data.events.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Tidpunkt</TableHead>
                      <TableHead className="w-[80px]">Nivå</TableHead>
                      <TableHead className="w-[70px]">Källa</TableHead>
                      <TableHead className="w-[120px]">Händelsenyckel</TableHead>
                      <TableHead>Meddelande</TableHead>
                      <TableHead className="w-[120px]">Producent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.events.map((event) => (
                      <TableRow 
                        key={event.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedEvent(event)}
                      >
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {formatTime(event.created_at)}
                        </TableCell>
                        <TableCell>
                          <SystemEventLevelBadge level={event.level} />
                        </TableCell>
                        <TableCell>
                          <SystemEventSourceBadge source={event.source} />
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                            {event.event_key}
                          </code>
                        </TableCell>
                        <TableCell className="max-w-[350px] truncate text-sm">
                          {event.message}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {event.producer?.name || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Visar {data.currentPage * data.pageSize + 1}–
                    {Math.min((data.currentPage + 1) * data.pageSize, data.totalCount)} av {data.totalCount} händelser
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Föregående
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= data.totalPages - 1}
                    >
                      Nästa
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <EmptyBlock
                title="Inga händelser"
                description="Inga händelser matchar dina filter. Prova att ändra tidsperiod eller ta bort filter."
                action={
                  hasActiveFilters ? (
                    <Button variant="outline" onClick={clearFilters}>
                      Rensa filter
                    </Button>
                  ) : undefined
                }
              />
            )}
        </StudioSurface>
      </div>

      {/* Event Detail Dialog */}
      <SystemEventDetailDialog
        event={selectedEvent}
        open={!!selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      />
    </AdminLayout>
  );
}
